/**
 * Practice Card Component
 * Displays core practice tools with status chips and better affordances
 */

'use client';

import { memo, useMemo } from 'react';
import Link from 'next/link';
import { Card } from '@/components/ui/card';
import { Target, ChevronRight } from 'lucide-react';
import { useActiveRitual } from '@/lib/hooks/useRitual';

const STAGE_LABELS: Record<string, string> = {
  prime: 'Composure',
  learn: 'Learning',
  voice: 'Voice',
  do: 'Action',
  note: 'Reflection',
} as const;

const STAGE_ORDER = ['prime', 'learn', 'voice', 'do', 'note'] as const;

export const PracticeCard = memo(function PracticeCard() {
  const { data: activeRitual } = useActiveRitual();

  const { hasActiveRitual, stageLabel, stageNumber } = useMemo(() => {
    const hasActive = activeRitual && !activeRitual.ended;
    const currentStage = activeRitual?.current_stage;
    // Only process stages that are in STAGE_ORDER (exclude 'done')
    const validStage = currentStage && currentStage !== 'done' ? currentStage : null;
    const label = validStage ? STAGE_LABELS[validStage] || validStage : null;
    const stageIndex = validStage
      ? STAGE_ORDER.indexOf(validStage as (typeof STAGE_ORDER)[number])
      : -1;
    const number = stageIndex >= 0 ? stageIndex + 1 : null;

    return {
      hasActiveRitual: hasActive,
      stageLabel: label,
      stageNumber: number,
    };
  }, [activeRitual]);

  return (
    <Card
      className="p-4 sm:p-5 md:p-6 lg:p-7 hover:shadow-xl transition-all border-l-4 border-l-primary hover:border-l-primary/80 shadow-sm"
      role="region"
      aria-label="Daily practice tools"
    >
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-5 flex items-center gap-2 sm:gap-3">
        <Target className="h-5 w-5 sm:h-6 sm:w-6 text-primary" aria-hidden="true" />
        <span className="text-foreground">Daily Practice</span>
      </h2>
      <nav aria-label="Practice options" className="space-y-2 sm:space-y-2.5">
        <Link
          href="/ritual"
          className="group flex items-center justify-between p-3.5 sm:p-4 rounded-xl hover:bg-primary/5 transition-all border border-border hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] sm:min-h-[64px]"
          aria-label={
            hasActiveRitual && stageLabel && stageNumber
              ? `Continue Daily Ritual - Stage ${stageNumber}: ${stageLabel}`
              : 'Start Daily Ritual'
          }
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 sm:gap-2.5 mb-1 sm:mb-1.5">
              <div className="font-semibold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors">
                Daily Ritual
              </div>
              {hasActiveRitual && stageLabel && stageNumber && (
                <span
                  className="text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                  aria-label={`Current stage: ${stageLabel}`}
                >
                  Stage {stageNumber}: {stageLabel}
                </span>
              )}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-0.5 sm:mt-1">
              {hasActiveRitual
                ? `Resume your practice • 5-stage guided flow`
                : '5-stage guided practice: Prime → Learn → Voice → Do → Note'}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-2 sm:ml-3">
            <span className="text-xs sm:text-sm text-primary font-semibold transition-colors whitespace-nowrap">
              {hasActiveRitual ? 'Continue' : 'Start'}
            </span>
            <ChevronRight
              className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1.5"
              aria-hidden="true"
            />
          </div>
        </Link>
        <Link
          href="/habits"
          className="group flex items-center justify-between p-3 sm:p-3.5 rounded-xl hover:bg-muted/50 transition-all border border-transparent hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[52px] sm:min-h-[56px]"
          aria-label="Open Habit Tracker"
        >
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors">
              Habit Tracker
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-0.5">
              Build values-linked habits
            </div>
          </div>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors transition-transform group-hover:translate-x-0.5 shrink-0 ml-2"
            aria-hidden="true"
          />
        </Link>
        <Link
          href="/review"
          className="group flex items-center justify-between p-3 sm:p-3.5 rounded-xl hover:bg-muted/50 transition-all border border-transparent hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[52px] sm:min-h-[56px]"
          aria-label="Open Weekly Review"
        >
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors">
              Weekly Review
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-0.5">
              Track your progress
            </div>
          </div>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors transition-transform group-hover:translate-x-0.5 shrink-0 ml-2"
            aria-hidden="true"
          />
        </Link>
      </nav>
    </Card>
  );
});
